import React, { useEffect, useRef, useState } from 'react';
import { usePostHog } from '@posthog/react';
import { useAuth } from '../../auth/AuthContext';
import { setToken, getEmail } from '../../auth/token';
import { loadGoogleIdentity, createNonce, googleClientId, exchangeGoogleCredential } from '../../auth/google';
import './AuthModal.css';

// Legal links for the agreement line under the Google option. Set either to
// null to hide the line.
const TERMS_URL = '/terms';
const PRIVACY_URL = '/privacy';

// Google Identity Services renders its "large" button 40px tall and at most
// 400px wide. We stretch that real button invisibly over our own tile so the
// click lands on Google's control (which opens the account chooser and returns
// the ID token our API verifies) while the user sees our tile.
const GIS_BUTTON_HEIGHT = 40;
const GIS_MAX_WIDTH = 400;

const GoogleLogo = () => (
    <svg className="auth-modal-google-logo" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
);

// Sign up / log in dialog. Google is the only way in; the two modes differ only
// in the title and the switch link at the bottom.
const AuthModal = ({ initialMode = 'login', onClose }) => {
    const { handleLoginSuccess } = useAuth();
    const posthog = usePostHog();
    const tileRef = useRef(null);
    const buttonRef = useRef(null);

    const [mode, setMode] = useState(initialMode);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => { setMode(initialMode); }, [initialMode]);

    const isSignup = mode === 'signup';

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    useEffect(() => {
        let cancelled = false;

        const signIn = async (credential, rawNonce) => {
            setBusy(true);
            setError('');
            try {
                setToken(await exchangeGoogleCredential(credential, rawNonce));
                const email = getEmail();
                try {
                    posthog.identify(email, { email });
                    posthog.capture('user_logged_in', { email, method: 'google' });
                } catch { /* posthog optional */ }
                handleLoginSuccess();
                onClose?.();
            } catch (err) {
                setError(err.message);
                try { posthog.capture('user_login_failed', { reason: err.message, method: 'google' }); } catch { /* ignore */ }
            } finally {
                if (!cancelled) setBusy(false);
            }
        };

        const renderButton = async () => {
            if (!googleClientId()) {
                setError('Google sign-in is not configured.');
                return;
            }
            try {
                const [googleId, nonce] = await Promise.all([loadGoogleIdentity(), createNonce()]);
                if (cancelled || !buttonRef.current || !tileRef.current) return;
                // Dismiss the bottom One Tap prompt if it's up; this dialog takes over.
                googleId.cancel();
                googleId.initialize({
                    client_id: googleClientId(),
                    nonce: nonce.hashed,
                    ux_mode: 'popup',
                    callback: (response) => signIn(response.credential, nonce.raw),
                });

                const tile = tileRef.current;
                const width = Math.min(GIS_MAX_WIDTH, Math.round(tile.offsetWidth));
                buttonRef.current.innerHTML = '';
                googleId.renderButton(buttonRef.current, {
                    type: 'standard',
                    theme: 'outline',
                    size: 'large',
                    text: 'continue_with',
                    width,
                });
                buttonRef.current.style.transform =
                    `scale(${tile.offsetWidth / width}, ${tile.offsetHeight / GIS_BUTTON_HEIGHT})`;
            } catch (err) {
                if (!cancelled) setError(err.message);
            }
        };

        renderButton();
        return () => { cancelled = true; };
    }, [handleLoginSuccess, onClose, posthog]);

    const title = isSignup ? 'Sign Up' : 'Log In';

    return (
        <div className="auth-modal-overlay" onClick={onClose}>
            <div
                className="auth-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="auth-modal-title"
            >
                <div className="auth-modal-head">
                    <h2 id="auth-modal-title" className="auth-modal-title">{title}</h2>
                    <button type="button" className="auth-modal-close" aria-label="Close" onClick={onClose}>
                        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <line x1="5" y1="5" x2="19" y2="19" />
                            <line x1="19" y1="5" x2="5" y2="19" />
                        </svg>
                    </button>
                </div>

                <div className="auth-modal-body">
                    <div ref={tileRef} className={`auth-modal-google${busy ? ' is-busy' : ''}`}>
                        <span className="auth-modal-google-face" aria-hidden="true">
                            <GoogleLogo />
                            <span>{busy ? 'Signing in…' : 'Continue with Google'}</span>
                        </span>
                        {/* Google's real button, invisible and stretched over the tile. */}
                        <div ref={buttonRef} className="auth-modal-google-hit" />
                    </div>

                    {error && <p className="auth-modal-error">{error}</p>}

                    {TERMS_URL && PRIVACY_URL && (
                        <p className="auth-modal-legal">
                            By {isSignup ? 'signing up' : 'continuing'}, you agree to the{' '}
                            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">user agreement</a>
                            {' '}and{' '}
                            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">privacy policy</a>
                        </p>
                    )}

                    <div className="auth-modal-switch">
                        {isSignup ? 'Already have an account?' : "Don't have an account?"}
                        <button
                            type="button"
                            className="auth-modal-switch-link"
                            onClick={() => { setError(''); setMode(isSignup ? 'login' : 'signup'); }}
                        >
                            {isSignup ? 'Log in' : 'Sign up'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AuthModal;
